import { Component, EventEmitter, Input, Output, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { map, Observable, startWith } from 'rxjs';

import { IAutocompleteOption } from 'app/models/filter.models';
import { MatFormField } from '@angular/material/form-field';
import { MultiselectSearchInputComponent } from './multiselect-search-input/multiselect-search-input.component';
import { MatDivider } from '@angular/material/divider';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';

@Component({
  selector: 'app-multiselect-with-search',
  templateUrl: './multiselect-with-search.component.html',
  styleUrls: ['./multiselect-with-search.component.scss'],
  imports: [MatFormField, MatSelect, MultiselectSearchInputComponent, MatDivider, NgIf, NgFor, MatOption, AsyncPipe],
})
export class MultiSelectWithSearchComponent implements ControlValueAccessor {
  @ViewChild('multiSelect') multiSelect!: MatSelect;

  @Input() placeholder = '';

  @Input() set options(value: IAutocompleteOption[]) {
    this.selectOptions = value;

    this.filteredOptions = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filter(value || '')),
    );
  }

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<void> = new EventEmitter<void>();

  @Input() disabled = false;

  searchControl = new FormControl('');

  selectOptions: IAutocompleteOption[] = [];
  filteredOptions!: Observable<IAutocompleteOption[]>;

  constructor(@Self() public controlDir: NgControl) {
    controlDir.valueAccessor = this;
  }

  onChange: (value: IAutocompleteOption[]) => void = () => {
    //no op
  };

  onTouched: () => void = () => {
    // no op
  };

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(values: IAutocompleteOption[]): void {
    if (!this.multiSelect) return;

    if (values?.length) {
      this.multiSelect.value = values.map(({ value }) => value);
    } else {
      this.multiSelect.value = [];
    }
  }

  registerOnChange = (fn: (value: IAutocompleteOption[]) => void) => {
    this.onChange = fn;
  };

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onSelectionChange(event: MatOptionSelectionChange) {
    if (!event.isUserInput) return;

    let values: IAutocompleteOption[] = this.controlDir.control?.value ?? [];
    const { value: selectedValue } = event.source;
    const selectedOption = this.selectOptions.find(({ value }) => value === selectedValue);

    if (event.source.selected && selectedOption && !values.includes(selectedOption)) {
      values.push(selectedOption);
    } else if (!event.source.selected && selectedOption) {
      values = values.filter(({ id }) => id !== selectedOption.id);
    }

    this.writeValue(values);
    this.onChange(values);
  }

  selectAll(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) return;

    const currentValues = this.controlDir.control?.value ?? [];
    const allSelected = currentValues.length === this.selectOptions.length;

    if (allSelected) {
      this.writeValue([]);
      this.onChange([]);
    } else {
      this.writeValue(this.selectOptions);
      this.onChange(this.selectOptions);
    }

    this.multiSelect.close();
  }

  private filter(value: string): IAutocompleteOption[] {
    const filterValue = value.toLowerCase();

    return this.selectOptions?.filter((option) => option.value.toLowerCase().includes(filterValue));
  }
}
